import {createRouter, createWebHistory} from "vue-router";
import type {RouteRecordRaw} from "vue-router";
import {useUserStore} from "@/stores/useUserStore";
import {useMaintenanceStore} from "@/stores/useMaintenanceStore.ts";

// Define your routes with type annotations
const routes: RouteRecordRaw[] = [
  {
    path: "/vermittlungsportal/",
    children: [
      {
        path: "",
        name: "Angebotssuche",
        component: () => import("@/views/Angebotssuche.vue"),
      },
      {
        path: "login",
        name: "Login",
        component: () => import("@/views/Login.vue"),
        meta: {guestOnly: true},
      },
      {
        path: "registration",
        name: "Registration",
        component: () => import("@/views/registration/Registration.vue"),
        meta: {guestOnly: true},
      },
      {
        path: "registration-bauernfamilie",
        name: "RegistrationBauernfamilie",
        component: () =>
          import("@/views/registration/RegistrationBauernfamilie.vue"),
        meta: {guestOnly: true},
      },
      {
        path: "registration-jugendlicher",
        name: "RegistrationJugendlicher",
        component: () =>
          import("@/views/registration/RegistrationJugendlicher.vue"),
        meta: {guestOnly: true},
      },
      {
        path: "passwort-vergessen",
        name: "PasswortVergessen",
        component: () => import("@/views/PasswortVergessen.vue"),
        meta: {guestOnly: true},
      },
      {
        path: "email-bestaetigen",
        name: "EmailBestaetigen",
        component: () => import("@/views/emailBestaetigen.vue"),
        meta: {guestOnly: true},
      },
      {
        path: "passwort-zuruecksetzen",
        name: "PasswortZuruecksetzen",
        component: () => import("@/views/passwortZuruecksetzen.vue"),
      },
      {
        path: "bestaetigung-gesetzliche-vertretung",
        name: "BestaetigungGesetzlicheVertretung",
        component: () =>
          import("@/views/BestaetigungGesetzlicheVertretung.vue"),
      },
      {
        path: "praktikum-detail/:id",
        name: "PraktikumDetail",
        component: () => import("@/views/PraktikumDetail.vue"),
      },
      {
        path: "einsatzuebersicht/:id",
        name: "Einsatzuebersicht",
        component: () => import("@/views/Einsatzuebersicht.vue"),
      },
      {
        path: "reservation",
        name: "Reservation",
        component: () => import("@/views/reservation/Reservation.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["jugendlicher"],
          logInAs: "jug",
        },
      },
      {
        path: "reservationsbestaetigung",
        name: "Reservationsbestaetigung",
        component: () =>
          import("@/views/reservation/Reservationsbestaetigung.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["jugendlicher"],
          logInAs: "jug",
        },
      },
      {
        path: "zahlung",
        name: "Zahlung",
        component: () => import("@/views/Zahlung.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["jugendlicher"],
          logInAs: "jug",
        },
      },
      {
        path: "angebotsuebersicht",
        name: "Angebotsuebersicht",
        component: () => import("@/views/Angebotsuebersicht.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["bauernfamilie"],
          logInAs: "bf",
        },
      },
      {
        path: "praktikum-anbieten/:id?",
        name: "PraktikumAnbieten",
        component: () => import("@/views/PraktikumAnbieten.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["bauernfamilie"],
          logInAs: "bf",
        },
      },
      {
        path: "profil",
        name: "Profil",
        component: () => import("@/views/profil/Profil.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["jugendlicher", "bauernfamilie"],
        },
      },
      {
        path: "profil-ro/:jugId/:einsatzId",
        name: "Profil-readOnly",
        component: () => import("@/views/profil/ProfilJugReadonly.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["jugendlicher", "bauernfamilie"],
        },
      },
      {
        path: "meine-einsaetze",
        name: "MeineEinsaetze",
        component: () => import("@/views/MeineEinsaetze.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["jugendlicher"],
          logInAs: "jug",
        },
      },
      {
        path: "meine-dokumente",
        name: "MeineDokumenteJug",
        component: () => import("@/views/dokumente/MeineDokumenteJug.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["jugendlicher"],
          logInAs: "jug",
        },
      },
      {
        path: "meine-dokumente",
        name: "MeineDokumenteBf",
        component: () => import("@/views/dokumente/MeineDokumenteBf.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["bauernfamilie"],
          logInAs: "bf",
        },
      },
      {
        path: "favoriten",
        name: "Favoriten",
        component: () => import("@/views/Favoriten.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["jugendlicher" /* , "bauernfamilie" */],
          logInAs: "jug",
        },
      },
      {
        path: "gesetzliche-vertretung-freigabe",
        name: "GesetzlicheVertretungFreigabe",
        component: () => import("@/views/GesetzlicheVertretungFreigabe.vue"),
        props: (route) => {
          const get = (v: unknown) => (Array.isArray(v) ? v[0] : (v ?? null));
          return {
            token: get(route.query.token) as string | null,
            einsatzId: get(route.query.einsatz) as string | null,
            person: get(route.query.person) as string | null,
          };
        },
        meta: {requiresToken: true},
      },
      {
        path: "abschlussformular",
        name: "Abschlussformular",
        component: () => import("@/views/Abschlussformular.vue"),
        props: (route) => {
          const get = (v: unknown) => (Array.isArray(v) ? v[0] : (v ?? null));
          return {
            token: get(route.query.token) as string | null,
            einsatzId: get(route.query.einsatz) as string | null,
          };
        },
        meta: {requiresToken: true},
      },
      {
        path: "wartungsarbeiten",
        name: "Wartungsarbeiten",
        component: () => import("@/views/Wartungsarbeiten.vue"),
      },
      {
        path: "/:pathMatch(.*)*",
        name: "NotFound",
        component: () => import("@/views/NotFound.vue"),
      },
      {
        path: "wartung",
        name: "Wartung",
        component: () => import("@/views/Wartungsarbeiten.vue"),
        meta: {maintenance: true},
      },
    ],
  },
];

// Create and export the router instance
const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    return new Promise((resolve) => {
      const isBack = !!savedPosition;
      const isHash = !!to.hash;

      // Smooth nur bei Vorwärts, aber nie bei Zurück oder Anker-Sprung
      document.documentElement.classList.toggle(
        "scroll-smooth",
        !isBack && !isHash,
      );

      if (isHash) {
        const el = document.querySelector(to.hash);
        if (el) {
          return resolve({el: to.hash, top: 70, behavior: "smooth"});
        }
        return resolve(false); // Kein zusätzliches Scrollen durch Vue
      }

      setTimeout(() => {
        if (isBack) {
          window.scrollTo({top: savedPosition.top, left: savedPosition.left});
        } else {
          window.scrollTo({top: 0, left: 0});
        }
        resolve(false);
      }, 0);
    });
  },
});

interface RouteMeta {
  guestOnly?: boolean;
  requiresAuth?: boolean;
  allowedRoles?: string[];
  logInAs?: string;
  requiresToken?: boolean;
  maintenance?: boolean;
}

router.beforeEach(async (to, _from, next) => {
  const meta = to.meta as RouteMeta;

  const maintenanceStore = useMaintenanceStore();

  // Wenn wir schon auf Wartung sind, nichts laden
  if (to.name === "Wartung") return next();

  // Status einmalig laden
  if (!maintenanceStore.loaded) {
    await maintenanceStore.load(); // via Status endpoint
  }

  if (maintenanceStore.enabled && to.name !== "Wartung") {
    return next({name: "Wartung", query: {redirect: to.fullPath}});
  }

  const userStore = useUserStore();
  const isLoggedIn = userStore.isLoggedIn;
  const userRole = userStore.role;

  if (meta.guestOnly && isLoggedIn) {
    return next({name: "Angebotssuche"});
  }

  if (meta.requiresAuth && !isLoggedIn) {
    // Uneingeloggt auf "Reservieren" geklickt: nach dem Login zurück auf die
    // Angebots-Detailseite statt aufs Reservationsformular. Die Query (angebot,
    // start, end) überlebt den Redirect nicht, und der Zeitraum muss ohnehin
    // neu gewählt werden, damit der Kalender-Check mit den frisch geladenen
    // reservedDates läuft.
    const angebot = to.name === "Reservation" ? to.query.angebot : undefined;
    const angebotId = Array.isArray(angebot) ? angebot[0] : angebot;
    sessionStorage.setItem(
      "redirectAfterLogin",
      angebotId
        ? `/vermittlungsportal/praktikum-detail/${angebotId}`
        : to.path,
    );
    return next({name: "Login", query: {typ: meta.logInAs}});
  }

  if (
    meta.requiresAuth &&
    meta.allowedRoles &&
    userRole &&
    !meta.allowedRoles.includes(userRole)
  ) {
    return next({name: "Angebotssuche"});
  }

  if (meta.requiresToken && !to.query.token) {
    return next({name: "Angebotssuche"});
  }

  return next();
});

export default router;
